import { useEffect, useState, useMemo } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CaretDown, CaretRight, MagnifyingGlass } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

export default function ChartOfAccounts() {
    const [tree, setTree] = useState([]);
    const [purchaseCats, setPurchaseCats] = useState([]);
    const [locations, setLocations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [openG, setOpenG] = useState(false);
    const [g, setG] = useState({ name: "", category: "indirect_expenses", party_type: "generic", is_direct: false });
    const [newLoc, setNewLoc] = useState("");
    const [newDept, setNewDept] = useState("");
    const [newCat, setNewCat] = useState("");
    const [search, setSearch] = useState("");
    const [expanded, setExpanded] = useState({}); // { "assets": true, "assets:Bank Accounts": true, ... }

    const load = async () => {
        const [tr, pc, loc, dep] = await Promise.all([
            api.get("/reports/chart-of-accounts"), api.get("/masters/purchase_categories"),
            api.get("/masters/locations"), api.get("/masters/departments"),
        ]);
        setTree(tr.data.tree || []);
        setPurchaseCats(pc.data); setLocations(loc.data); setDepartments(dep.data);
        // First load: expand all categories, keep groups collapsed
        setExpanded((prev) => {
            const next = { ...prev };
            (tr.data.tree || []).forEach((c) => { if (next[c.category] === undefined) next[c.category] = true; });
            return next;
        });
    };
    useEffect(() => { load(); }, []);

    const addGroup = async () => {
        try { await api.post("/account-groups", g); toast.success("Group added"); setOpenG(false); setG({ name: "", category: "indirect_expenses", party_type: "generic", is_direct: false }); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const addMaster = async (kind, name, clear) => {
        if (!name) return;
        try { await api.post(`/masters/${kind}`, { name }); toast.success("Added"); clear(""); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const editMaster = async (kind, id, name) => {
        try { await api.patch(`/masters/${kind}/${id}`, { name }); toast.success("Renamed"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const deleteMaster = async (kind, id, name) => {
        if (!window.confirm(`Delete "${name}"? This is only allowed if nothing references it.`)) return;
        try { await api.delete(`/masters/${kind}/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const editGroup = async (gid, currentName, currentCategory) => {
        const newName = window.prompt("Rename group", currentName);
        if (!newName || newName.trim() === currentName) return;
        try { await api.patch(`/account-groups/${gid}`, { name: newName.trim(), category: currentCategory }); toast.success("Group renamed"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const deleteGroup = async (gid, name) => {
        if (!window.confirm(`Delete group "${name}"? Only allowed if no ledgers are inside.`)) return;
        try { await api.delete(`/account-groups/${gid}`); toast.success("Group deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const catColors = {
        assets: { fg: "#0f766e", bg: "#ccfbf1" },        // teal
        liabilities: { fg: "#b45309", bg: "#fef3c7" },   // amber
        income: { fg: "#166534", bg: "#dcfce7" },        // green
        direct_cost: { fg: "#7e22ce", bg: "#f3e8ff" },   // purple
        indirect_expenses: { fg: "#0369a1", bg: "#dbeafe" }, // blue
    };

    // Filter tree by search term (matches ledger name or group name)
    const filteredTree = useMemo(() => {
        const q = (search || "").trim().toLowerCase();
        if (!q) return tree;
        return tree
            .map((cat) => {
                const groups = cat.groups
                    .map((g) => {
                        const groupMatches = (g.name || "").toLowerCase().includes(q);
                        const ledgers = g.ledgers.filter((l) => (l.name || "").toLowerCase().includes(q));
                        if (groupMatches) return g;   // group name match → include whole group
                        if (ledgers.length) return { ...g, ledgers };
                        return null;
                    })
                    .filter(Boolean);
                if (!groups.length) return null;
                return { ...cat, groups, ledger_count: groups.reduce((s, g) => s + g.ledgers.length, 0) };
            })
            .filter(Boolean);
    }, [tree, search]);

    // Auto-expand all matches when searching
    useEffect(() => {
        if (!search) return;
        const next = { ...expanded };
        filteredTree.forEach((c) => {
            next[c.category] = true;
            c.groups.forEach((g) => { next[`${c.category}:${g.name}`] = true; });
        });
        setExpanded(next);
    }, [search]);

    const toggleCat = (k) => setExpanded((e) => ({ ...e, [k]: !e[k] }));
    const toggleGroup = (catKey, gname) => setExpanded((e) => ({ ...e, [`${catKey}:${gname}`]: !e[`${catKey}:${gname}`] }));

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Chart of accounts" sub="Every ledger organised into Assets, Liabilities, Income, Direct cost and Indirect expenses — with current balances."
                right={
                    <Dialog open={openG} onOpenChange={setOpenG}>
                        <DialogTrigger asChild><Button style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="add-group-btn">+ New group</Button></DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-serif text-2xl">New group</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div><Label>Name</Label><Input value={g.name} onChange={(e) => setG({ ...g, name: e.target.value })} data-testid="grp-name" /></div>
                                <div><Label>Category</Label>
                                    <Select value={g.category} onValueChange={(v) => setG({ ...g, category: v })}><SelectTrigger data-testid="grp-cat"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="assets">Assets</SelectItem>
                                            <SelectItem value="liabilities">Liabilities</SelectItem>
                                            <SelectItem value="income">Income</SelectItem>
                                            <SelectItem value="direct_cost">Direct cost</SelectItem>
                                            <SelectItem value="indirect_expenses">Indirect expenses</SelectItem>
                                        </SelectContent></Select>
                                </div>
                                <div><Label>Party type</Label>
                                    <Select value={g.party_type} onValueChange={(v) => setG({ ...g, party_type: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent><SelectItem value="generic">Generic</SelectItem><SelectItem value="vendor">Vendor</SelectItem><SelectItem value="customer">Customer</SelectItem><SelectItem value="transporter">Transporter</SelectItem><SelectItem value="bank">Bank</SelectItem><SelectItem value="cash">Cash</SelectItem></SelectContent></Select>
                                </div>
                            </div>
                            <DialogFooter><Button onClick={addGroup} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="grp-save">Create</Button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                }/>
            <div className="p-8 grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2 space-y-3">
                    <div className="relative">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                        <Input
                            className="pl-9"
                            placeholder="Search a ledger or group…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="coa-search"
                        />
                    </div>
                    {filteredTree.length === 0 ? (
                        <div className="bg-white border rounded-md p-8 text-center text-stone-500 text-sm" style={{ borderColor: "var(--se-border)" }}>
                            {search ? "No ledgers match your search." : "Loading…"}
                        </div>
                    ) : null}
                    {filteredTree.map((cat) => {
                        const c = catColors[cat.category] || { fg: "#374151", bg: "#f3f4f6" };
                        const isOpen = !!expanded[cat.category];
                        return (
                            <div key={cat.category} className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid={`coa-cat-${cat.category}`}>
                                <button
                                    className="w-full px-5 py-3 border-b flex items-center justify-between text-left hover:bg-stone-50"
                                    style={{ borderColor: "var(--se-border)" }}
                                    onClick={() => toggleCat(cat.category)}
                                    data-testid={`coa-cat-toggle-${cat.category}`}
                                >
                                    <div className="flex items-center gap-2">
                                        {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
                                        <span className="text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full"
                                              style={{ background: c.bg, color: c.fg }}>{cat.label}</span>
                                        <span className="text-xs text-stone-500">{cat.ledger_count} ledger{cat.ledger_count === 1 ? "" : "s"}</span>
                                    </div>
                                    <div className="font-mono text-sm" style={{ color: c.fg }}>
                                        ₹ {inr(Math.abs(cat.total))}
                                    </div>
                                </button>
                                {isOpen ? (
                                    <div className="p-2">
                                        {cat.groups.map((g) => {
                                            const gKey = `${cat.category}:${g.name}`;
                                            const gOpen = !!expanded[gKey];
                                            return (
                                                <div key={g.name} className="border-b last:border-b-0 group/row" style={{ borderColor: "var(--se-border)" }}>
                                                    <div className="w-full px-3 py-2 flex items-center justify-between text-sm hover:bg-stone-50">
                                                        <button
                                                            className="flex items-center gap-2 flex-1 text-left"
                                                            onClick={() => toggleGroup(cat.category, g.name)}
                                                            data-testid={`coa-grp-toggle-${g.name}`}
                                                        >
                                                            {gOpen ? <CaretDown size={12} className="text-stone-400" /> : <CaretRight size={12} className="text-stone-400" />}
                                                            <span className="font-medium">{g.name}</span>
                                                            <span className="text-[11px] text-stone-400">{g.ledgers.length}</span>
                                                        </button>
                                                        <div className="flex items-center gap-2">
                                                            {g.group_id ? (
                                                                <div className="opacity-0 group-hover/row:opacity-100 transition-opacity flex gap-1">
                                                                    <button onClick={() => editGroup(g.group_id, g.name, g.category)} className="text-[10px] text-stone-500 hover:text-stone-800 px-1" data-testid={`coa-grp-edit-${g.group_id}`}>EDIT</button>
                                                                    <button onClick={() => deleteGroup(g.group_id, g.name)} className="text-[10px] text-rose-700 hover:text-rose-900 px-1" data-testid={`coa-grp-del-${g.group_id}`}>DELETE</button>
                                                                </div>
                                                            ) : null}
                                                            <div className="font-mono text-xs text-stone-500 min-w-[100px] text-right">
                                                                ₹ {inr(Math.abs(g.subtotal))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {gOpen ? (
                                                        <ul className="pb-2">
                                                            {g.ledgers.map((l) => (
                                                                <li key={l.id} className="pl-9 pr-3 py-1.5 flex items-center justify-between text-[13px] hover:bg-stone-50" data-testid={`coa-ledger-${l.id}`}>
                                                                    <a href={`/ledgers/${l.id}`} className="hover:underline" style={{ color: "var(--se-brand)" }}>
                                                                        {l.name}
                                                                        {l.gstin ? <span className="ml-2 text-[10px] text-stone-400 font-mono">{l.gstin}</span> : null}
                                                                    </a>
                                                                    <div className="flex items-center gap-2 font-mono text-xs">
                                                                        {l.is_negative ? <span className="text-[10px] uppercase text-rose-700">flipped</span> : null}
                                                                        <span style={{ color: l.balance === 0 ? "#a8a29e" : (l.is_negative ? "#9f1239" : "#1c1917") }}>
                                                                            {l.is_negative ? "− " : ""}₹ {inr(l.balance)}
                                                                        </span>
                                                                        <span className="text-[10px] uppercase text-stone-400 w-6 text-right">{l.side}</span>
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : null}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
                <div className="space-y-6">
                    <MasterCard title="Locations (cost centre)" items={locations} value={newLoc} setValue={setNewLoc}
                                onAdd={() => addMaster("locations", newLoc, setNewLoc)}
                                onEdit={(id, name) => editMaster("locations", id, name)}
                                onDelete={(id, name) => deleteMaster("locations", id, name)}
                                testid="master-locations" />
                    <MasterCard title="Departments (cost centre)" items={departments} value={newDept} setValue={setNewDept}
                                onAdd={() => addMaster("departments", newDept, setNewDept)}
                                onEdit={(id, name) => editMaster("departments", id, name)}
                                onDelete={(id, name) => deleteMaster("departments", id, name)}
                                testid="master-depts" />
                    <MasterCard title="Purchase categories" items={purchaseCats} value={newCat} setValue={setNewCat}
                                onAdd={() => addMaster("purchase_categories", newCat, setNewCat)}
                                onEdit={(id, name) => editMaster("purchase_categories", id, name)}
                                onDelete={(id, name) => deleteMaster("purchase_categories", id, name)}
                                testid="master-cats" />
                </div>
            </div>
        </>
    );
}

function MasterCard({ title, items, value, setValue, onAdd, onEdit, onDelete, testid }) {
    const [editing, setEditing] = useState(null); // { id, name }
    const startEdit = (item) => setEditing({ id: item.id, name: item.name });
    const saveEdit = async () => {
        if (!editing?.name?.trim()) return setEditing(null);
        await onEdit(editing.id, editing.name.trim());
        setEditing(null);
    };
    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid={testid}>
            <div className="px-5 py-3 border-b font-serif text-lg" style={{ borderColor: "var(--se-border)" }}>{title}</div>
            <div className="p-4">
                <div className="flex flex-wrap gap-2 mb-3">
                    {items.length === 0 ? <div className="text-xs text-stone-400">Nothing here yet.</div> : null}
                    {items.map((x) => {
                        if (editing?.id === x.id) {
                            return (
                                <div key={x.id} className="flex gap-1 items-center border rounded-sm px-1 py-0.5" style={{ borderColor: "var(--se-brand)" }}>
                                    <Input
                                        autoFocus
                                        value={editing.name}
                                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditing(null); }}
                                        className="h-6 text-xs w-40 border-0 focus-visible:ring-0"
                                        data-testid={`${testid}-edit-input`}
                                    />
                                    <button onClick={saveEdit} className="text-[11px] px-1.5 text-white rounded-sm" style={{ background: "var(--se-brand)" }}>✓</button>
                                    <button onClick={() => setEditing(null)} className="text-[11px] px-1.5 text-stone-500">✕</button>
                                </div>
                            );
                        }
                        return (
                            <div key={x.id} className="group flex items-center gap-1 border rounded-full pl-2 pr-1 py-0.5 hover:bg-stone-50" style={{ borderColor: "var(--se-border)" }} data-testid={`${testid}-item-${x.id}`}>
                                <span className="se-tag" style={{ background: "transparent", padding: 0, fontSize: 11 }}>{x.name}</span>
                                <button
                                    onClick={() => startEdit(x)}
                                    className="text-[9px] opacity-0 group-hover:opacity-100 transition-opacity text-stone-500 hover:text-stone-800 px-1"
                                    title="Rename"
                                    data-testid={`${testid}-edit-${x.id}`}
                                >EDIT</button>
                                <button
                                    onClick={() => onDelete(x.id, x.name)}
                                    className="text-[9px] opacity-0 group-hover:opacity-100 transition-opacity text-rose-700 hover:text-rose-900 px-1"
                                    title="Delete (only allowed if unused)"
                                    data-testid={`${testid}-del-${x.id}`}
                                >×</button>
                            </div>
                        );
                    })}
                </div>
                <div className="flex gap-2">
                    <Input placeholder="Add new…" value={value} onChange={(e) => setValue(e.target.value)} className="h-8" />
                    <Button size="sm" onClick={onAdd} style={{ background: "var(--se-brand)", color: "#fff" }}>Add</Button>
                </div>
            </div>
        </div>
    );
}
